"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import Layout from "../appcomponents/Layout";
import OrderFlow from "../orders/OrderFlow";
import { SYMBOL_IMAGES, OFFERED_SYMBOL_IDS } from "../appcomponents/thankYouCard";
import {
  EXAMPLE_PAGE_HREF,
  FOOTER_SLIDES,
  FOOTER_TITLE,
  NOVOSTI_DATE,
  POPUPS,
  SECTIONS,
} from "./novostiTexts";

const HEADER = {
  navy: "bg-[#112D77]",
  red: "bg-[#6E0A0A]",
  purple: "bg-[#640F6C]",
  green: "bg-[#105310]",
};

// A red "?" that opens something (a popup of this page or the order popup of a product).
function Help({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="ml-1 inline-flex align-top text-[16px] font-bold leading-none text-[#EB1D1D] hover:opacity-70"
    >
      ?
    </button>
  );
}

function InfoBox({ box }) {
  return (
    <div className="max-w-[300px] rounded-[6px] bg-[#E8F0F6] p-4 text-[#6D778E] mobile:hidden">
      <strong className="flex items-center gap-1 text-[13px] font-medium text-[#112D77]">
        <span aria-hidden="true" className="inline-flex h-[14px] w-[14px] items-center justify-center rounded-full border border-[#112D77] text-[9px]">
          ?
        </span>
        {box.title}
      </strong>
      {box.lines?.map((line) => (
        <p key={line} className="mt-2 text-[11px] leading-[16px]">
          {line}
        </p>
      ))}
      {box.bullets ?
        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-[16px]">
          {box.bullets.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      : null}
    </div>
  );
}

function Symbols({ extraText }) {
  return (
    <div className="rounded-[6px] bg-[#E8F0F6] px-5 py-3 text-center mobile:bg-transparent mobile:p-0">
      <div className="flex items-center justify-center gap-5 mobile:gap-2">
        {OFFERED_SYMBOL_IDS.map((id) => (
          <Image key={id} src={SYMBOL_IMAGES[id].src} alt={SYMBOL_IMAGES[id].label} width={26} height={30} className="h-[30px] w-auto mobile:h-[16px]" />
        ))}
      </div>
      <p className="mt-2 text-[11px] text-[#6D778E] mobile:hidden">Prikazan bo v seznamu osmrtnic in na žalni strani</p>
      {extraText ? <p className="mt-1 hidden text-right text-[13px] text-[#112D77] mobile:block">{extraText}</p> : null}
    </div>
  );
}

function Right({ right, onPopup, onOrder }) {
  if (!right) return null;
  switch (right.type) {
    case "text":
      return <span className="text-[14px] text-[#112D77] mobile:text-[13px]">{right.text}</span>;
    case "link":
      return (
        <Link href={right.href} className="text-[14px] text-[#0A85C2] underline mobile:text-[13px]">
          {right.text}
        </Link>
      );
    case "popup":
      return (
        <span className="text-[14px] text-[#112D77] mobile:text-[13px]">
          {right.text}
          <Help label={right.text} onClick={() => onPopup(right.popup)} />
        </span>
      );
    case "order":
      return (
        <span className="text-[14px] text-[#112D77]">
          {right.text}
          <Help label={right.text} onClick={() => onOrder(right.product)} />
        </span>
      );
    case "box":
      return <InfoBox box={right} />;
    case "symbols":
      return <Symbols extraText={right.extraText} />;
    case "help":
      return <Help label="Več informacij" onClick={() => (right.popup ? onPopup(right.popup) : onOrder(right.order))} />;
    default:
      return null;
  }
}

// A text that differs on phones: both are drawn, the screen width decides which one is seen.
function Dual({ desktop, phone, children }) {
  if (phone === undefined || phone === desktop) return children(desktop);
  return (
    <>
      <span className="mobile:hidden">{children(desktop)}</span>
      <span className="hidden mobile:inline">{children(phone)}</span>
    </>
  );
}

function Row({ row, onPopup, onOrder }) {
  const phone = row.phone || {};
  const phoneRight = "right" in phone ? phone.right : row.right;
  const rightDiffers = "right" in phone;
  const titleHelp = "titleHelp" in phone ? phone.titleHelp : row.titleHelp;
  return (
    <div className="flex min-h-[67px] items-center justify-between gap-6 border-t border-[#E3E8EE] bg-[#FBFCFE] px-6 py-3 first:border-t-0 mobile:flex-col mobile:items-start mobile:gap-1 mobile:px-4">
      <div className="min-w-0">
        <h3 className="text-[16px] leading-[22px] text-[#1E2125]">
          <Dual desktop={row.title} phone={phone.title}>
            {(text) => text}
          </Dual>
          {row.titleHelp ?
            <span className={titleHelp ? "" : "mobile:hidden"}>
              <Help label={row.title} onClick={() => onOrder(row.titleHelp.order)} />
            </span>
          : null}
        </h3>
        <p className="mt-[2px] text-[12px] leading-[16px] text-[#7C8494]">
          <Dual desktop={row.sub} phone={phone.sub}>
            {(text) => text}
          </Dual>
          {row.exampleLink ?
            <>
              {" "}
              <Link href={EXAMPLE_PAGE_HREF} className="text-[#0A85C2] underline mobile:hidden">
                {row.exampleLink}
              </Link>
            </>
          : null}
          {row.phoneHelp ?
            <span className="hidden mobile:inline">
              <Help label={row.title} onClick={() => onPopup(row.phoneHelp.popup)} />
            </span>
          : null}
        </p>
      </div>
      <div className="shrink-0 mobile:self-end">
        {rightDiffers ?
          <>
            <div className="mobile:hidden">
              <Right right={row.right} onPopup={onPopup} onOrder={onOrder} />
            </div>
            <div className="hidden mobile:block">
              <Right right={phoneRight} onPopup={onPopup} onOrder={onOrder} />
            </div>
          </>
        : <Right right={row.right} onPopup={onPopup} onOrder={onOrder} />}
      </div>
    </div>
  );
}

function Section({ section, onPopup, onOrder }) {
  return (
    <section className="w-full">
      <div className={`${HEADER[section.tone]} flex h-[48px] items-center px-6 text-[16px] uppercase text-white mobile:px-4`}>
        <Dual desktop={section.title} phone={section.phoneTitle}>
          {(text) => text}
        </Dual>
      </div>
      <div className="border-b-2 border-[#073545] shadow-md">
        {section.rows.map((row) => (
          <Row key={row.title} row={row} onPopup={onPopup} onOrder={onOrder} />
        ))}
      </div>
    </section>
  );
}

function PopupDialog({ id, onClose, onOrder }) {
  const popup = POPUPS[id];
  if (!popup) return null;
  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-[#000000B3] px-3"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div role="dialog" aria-modal="true" aria-label={popup.title} className="w-full max-w-[420px] rounded-[10px] border border-[#8FB6E2] bg-[#D2E7FB] p-6 text-[#1E2125] shadow-lg">
        <h2 className="flex items-center gap-2 text-[18px] text-[#112D77]">
          <span aria-hidden="true" className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#112D77] text-[11px]">
            ?
          </span>
          {popup.title}
        </h2>
        {popup.paragraphs?.map((text) => (
          <p key={text} className="mt-4 text-[15px] leading-[22px]">
            {text}
          </p>
        ))}
        {popup.bullets ?
          <ul className="mt-3 list-disc space-y-1 pl-6 text-[15px] leading-[22px]">
            {popup.bullets.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        : null}
        {popup.link ?
          <p className="mt-4 text-[15px]">
            <Link href={popup.link.href} className="text-[#0A85C2] underline">
              {popup.link.text}
            </Link>
          </p>
        : null}
        {popup.after?.map((text) => (
          <p key={text} className="mt-4 text-[15px] leading-[22px]">
            {text}
          </p>
        ))}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {popup.order ?
            <button
              type="button"
              onClick={() => {
                onClose();
                onOrder(popup.order.product);
              }}
              className="h-[40px] rounded-[6px] bg-[#0A85C2] px-6 text-[15px] text-white"
            >
              {popup.order.text}
            </button>
          : null}
          <button type="button" onClick={onClose} className="h-[40px] rounded-[6px] border border-[#112D77] px-6 text-[15px] text-[#112D77]">
            Zapri
          </button>
        </div>
      </div>
    </div>
  );
}

// "Prvo leto je za nami": a slider with a few numbers, and a closing message as the last slide.
function FooterSlider() {
  const [index, setIndex] = useState(0);
  const go = (step) => setIndex((current) => (current + step + FOOTER_SLIDES.length) % FOOTER_SLIDES.length);
  // The slides change by themselves every 10 seconds; a manual click restarts the countdown.
  useEffect(() => {
    const timer = setTimeout(() => go(1), 10000);
    return () => clearTimeout(timer);
  }, [index]);
  const shadow = { textShadow: "1px 2px 3px rgba(0,0,0,0.25)" };
  const lines = (list) =>
    list.map((line) => (
      <p key={line} className="mt-3 text-[16px] leading-[22px] text-white/90 mobile:text-[14px]">
        {line}
      </p>
    ));
  // One slide with its title and bottom bar. All slides are laid on top of each other in one grid cell and only the
  // current one is visible, so the footer is always as tall as the tallest slide and nothing jumps when it changes.
  const view = (slide, current) => (
    <div
      key={slide.big || "closing"}
      aria-hidden={!current}
      className={`flex min-h-full flex-col [grid-area:1/1] ${current ? "" : "invisible pointer-events-none"}`}
    >
      {slide.closing ? null : (
        <h2 style={shadow} className="text-[34px] font-bold leading-[40px] text-[#A8B9CC] mobile:text-[24px]">
          {FOOTER_TITLE}
        </h2>
      )}
      <div className="mx-auto mt-10 flex w-full max-w-[860px] flex-1 items-center justify-between gap-2 mobile:mt-8">
        <button type="button" tabIndex={current ? 0 : -1} aria-label="Prejšnja številka" onClick={() => go(-1)} className="px-2 text-[44px] leading-none text-white/50 hover:text-white">
          ‹
        </button>
        {slide.closing ?
          <div className="min-w-0 py-4">
            {slide.paragraphs.map((group, i) => (
              <p key={i} className={`${i ? "mt-6" : ""} text-[18px] leading-[28px] text-white/90 mobile:text-[15px] mobile:leading-[24px]`}>
                {group.map((line, j) => (
                  <span key={line}>
                    {j ? <br /> : null}
                    {line}
                  </span>
                ))}
              </p>
            ))}
            <p style={shadow} className="mt-14 text-[40px] font-bold leading-[48px] text-[#A8B9CC] mobile:mt-8 mobile:text-[26px] mobile:leading-[32px]">
              {slide.headline}
            </p>
          </div>
        : <div className="min-w-0">
            <div
              style={shadow}
              className="mx-auto inline-block rounded-[6px] border border-[#9CC3E0] bg-[#CFE9FB] px-10 py-2 text-[46px] font-bold leading-[54px] text-[#6C93B6] mobile:text-[40px]"
            >
              {slide.big}
            </div>
            {slide.phoneLines ?
              <>
                <div className="mobile:hidden">{lines(slide.lines)}</div>
                <div className="hidden mobile:block">{lines(slide.phoneLines)}</div>
              </>
            : lines(slide.lines)}
          </div>}
        <button type="button" tabIndex={current ? 0 : -1} aria-label="Naslednja številka" onClick={() => go(1)} className="px-2 text-[44px] leading-none text-white/50 hover:text-white">
          ›
        </button>
      </div>
      <div className="mx-auto mt-12 flex w-full max-w-[860px] items-center justify-between border-t border-white/40 pt-4 text-[12px] text-white/80 mobile:mt-8 mobile:flex-col mobile:items-stretch mobile:gap-3 mobile:text-[13px]">
        <span className="mobile:hidden">© osmrtnica.com</span>
        {slide.closing ?
          <span className="text-[16px] text-white/90 mobile:order-first mobile:text-center mobile:text-[14px]">{slide.thanks}</span>
        : <span className="flex gap-2 mobile:order-first mobile:justify-center" role="tablist" aria-label="Številke">
            {FOOTER_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                tabIndex={current ? 0 : -1}
                aria-selected={i === index}
                aria-label={`Številka ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-[12px] w-[12px] rounded-full ${i === index ? "bg-[#6FA3C7]" : "bg-[#D8DDE2]"}`}
              />
            ))}
          </span>}
        <Link href="/" tabIndex={current ? 0 : -1} className="text-[14px] mobile:self-start">
          Prva stran
        </Link>
      </div>
    </div>
  );
  return (
    <footer className="mt-[100px] w-full bg-[#36556C] px-4 pb-8 pt-12 text-center text-white mobile:mt-[70px]">
      <div className="grid">{FOOTER_SLIDES.map((item, i) => view(item, i === index))}</div>
    </footer>
  );
}

/** "Novosti in izboljšave": what is new on the site, in four blocks, with a few numbers at the bottom. */
export default function NovostiContent() {
  const [popup, setPopup] = useState(null);
  const [orderProduct, setOrderProduct] = useState(null);

  return (
    <Layout from={"23"} forFooter={"memorypage"} heroTitle="Novosti in izboljšave" heroImage="/novosti-hero.jpg">
      <div className="flex w-full flex-col items-center bg-[#ECF0F3]">
        {/* Phones (up to 743px): the content is the width of the screen minus 10px each side, but never wider than 440px. From 744px: 683px. */}
        <div className="w-full max-w-[460px] px-[10px] pb-4 pt-[44px] [@media(min-width:744px)]:max-w-[741px] [@media(min-width:744px)]:px-[29px]">
          <p className="mb-2 text-right text-[11px] text-[#7C8494] mobile:hidden">{NOVOSTI_DATE}</p>
          <div className="flex flex-col gap-y-[56px] mobile:gap-y-[48px]">
            {SECTIONS.map((section) => (
              <Section key={section.title} section={section} onPopup={setPopup} onOrder={setOrderProduct} />
            ))}
          </div>
        </div>
        <FooterSlider />

        {/* UP = scroll to the top (phones) */}
        <button
          type="button"
          aria-label="Na vrh strani"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-4 z-40 hidden h-[48px] w-[48px] items-center justify-center rounded-lg bg-white/90 text-[22px] shadow-lg mobile:flex"
        >
          ↑
        </button>
      </div>

      <PopupDialog id={popup} onClose={() => setPopup(null)} onOrder={setOrderProduct} />
      {orderProduct && <OrderFlow product={orderProduct} onClose={() => setOrderProduct(null)} />}
    </Layout>
  );
}
